import { IMat4 } from "./Mat4";
import { MathUtil, Radian } from "./MathUtil";
import { IQuat } from "./Quat";

export interface IVec3 {
  x: number;
  y: number;
  z: number;
}

export class Vec3 {
  static fromNumber(n: number): IVec3 {
    return { x: n, y: n, z: n };
  }

  static from(x: number, y: number, z: number): IVec3 {
    return { x, y, z };
  }

  static fromXY(v: { x: number, y: number }, newY: number = 0.0): IVec3 {
    return Vec3.from(v.x, newY, v.y);
  }

  static fromSpherical(phi: number, theta: number, radius: number = 1): IVec3 {
    const sinPhiRadius = Math.sin(phi) * radius;

    return Vec3.from(
      sinPhiRadius * Math.sin(theta),
      Math.cos(phi) * radius,
      sinPhiRadius * Math.cos(theta)
    );
  }

  static toTuple(v: IVec3): [number, number, number] {
    return [v.x, v.y, v.z];
  }

  static clone(a: IVec3): IVec3 {
    return { x: a.x, y: a.y, z: a.z };
  }

  static add(a: IVec3, b: IVec3): IVec3 {
    return {
      x: a.x + b.x,
      y: a.y + b.y,
      z: a.z + b.z,
    };
  }

  static adds(...rest: (IVec3 | undefined)[]): IVec3 {
    return Vec3.addArray(rest);
  }

  static addArray(vecs: (IVec3 | undefined)[]): IVec3 {
    if (vecs.length < 1)
      return Vec3.ZERO;

    let current = vecs[vecs.length - 1] || Vec3.ZERO;
    for (let i = vecs.length - 2; i >= 0; i--) {
      const v = vecs[i];
      if (v)
        current = Vec3.add(v, current);
    }
    return current;
  }

  static addElements(a: IVec3, x: number, y: number, z: number): IVec3 {
    return {
      x: a.x + x,
      y: a.y + y,
      z: a.z + z,
    };
  }

  static addY(a: IVec3, y: number): IVec3 {
    return {
      x: a.x,
      y: a.y + y,
      z: a.z,
    };
  }

  static withY(a: IVec3, y: number): IVec3 {
    return {
      x: a.x,
      y: y,
      z: a.z,
    };
  }


  static addScaled(a: IVec3, b: IVec3, scalar: number): IVec3 {
    return {
      x: a.x + b.x * scalar,
      y: a.y + b.y * scalar,
      z: a.z + b.z * scalar,
    };
  }

  static dot(a: IVec3, b: IVec3): number {
    return a.x * b.x + a.y * b.y + a.z * b.z;
  }

  static absDot(a: IVec3, b: IVec3): number {
    return Math.abs(a.x * b.x + a.y * b.y + a.z * b.z);
  }

  static project(a: IVec3, onto: IVec3): IVec3 {
    const normalizedOnto = Vec3.normalized(onto);
    const scale = Vec3.dot(a, normalizedOnto);
    return Vec3.mul(normalizedOnto, scale);
  }

  static assign(target: IVec3, from: IVec3): void {
    target.x = from.x;
    target.y = from.y;
    target.z = from.z;
  }

  static get empty(): IVec3 {
    return { x: 0, y: 0, z: 0 };
  }

  static sub(a: IVec3, b: IVec3): IVec3 {
    return {
      x: a.x - b.x,
      y: a.y - b.y,
      z: a.z - b.z,
    };
  }

  static subElements(a: IVec3, x: number, y: number, z: number): IVec3 {
    return {
      x: a.x - x,
      y: a.y - y,
      z: a.z - z,
    };
  }

  static to(a: IVec3, b: IVec3): IVec3 {
    return Vec3.sub(b, a);
  }

  static equals(a: IVec3, b: IVec3): boolean {
    if (a === b) return true;
    if (!a && b) return false;
    if (!b && a) return false;
    return a.x === b.x && a.y === b.y && a.z === b.z;
  }

  static dir(a: IVec3, b: IVec3): IVec3 {
    return Vec3.normalized(Vec3.sub(b, a));
  }

  static dirLen(a: IVec3, b: IVec3): { dir: IVec3, len: number } {
    const ab = Vec3.to(a, b);
    const len = Vec3.len(a, b);
    return {
      dir: Vec3.mul(ab, 1 / len),
      len
    }
  }

  static mul(a: IVec3, s: number): IVec3 {
    return {
      x: a.x * s,
      y: a.y * s,
      z: a.z * s,
    };
  }

  static scale(a: IVec3, s: IVec3): IVec3 {
    return {
      x: a.x * s.x,
      y: a.y * s.y,
      z: a.z * s.z,
    }
  }

  static mid(a: IVec3, b: IVec3): IVec3 {
    return {
      x: (a.x + b.x) * 0.5,
      y: (a.y + b.y) * 0.5,
      z: (a.z + b.z) * 0.5,
    };
  }

  static lerp(a: IVec3, b: IVec3, posFactor: number): IVec3 {
    return {
      x: a.x + (b.x - a.x) * posFactor,
      y: a.y + (b.y - a.y) * posFactor,
      z: a.z + (b.z - a.z) * posFactor,
    };
  }

  static cross(a: IVec3, b: IVec3): IVec3 {
    return {
      x: a.y * b.z - a.z * b.y,
      y: a.z * b.x - a.x * b.z,
      z: a.x * b.y - a.y * b.x,
    };
  }

  static crossNormalized(a: IVec3, b: IVec3): IVec3 {
    const c = {
      x: a.y * b.z - a.z * b.y,
      y: a.z * b.x - a.x * b.z,
      z: a.x * b.y - a.y * b.x,
    };
    return Vec3.normalized(c);
  }

  static len(a: IVec3, b?: IVec3): number {
    if (b !== undefined) {
      const ab = this.sub(b, a);
      return Math.sqrt(ab.x * ab.x + ab.y * ab.y + ab.z * ab.z);
    }
    return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
  }

  static len2(a: IVec3, b?: IVec3): number {
    if (b !== undefined) {
      const ab = this.sub(b, a);
      return ab.x * ab.x + ab.y * ab.y + ab.z * ab.z;
    }
    return a.x * a.x + a.y * a.y + a.z * a.z;
  }

  static withLen(a: IVec3, len: number): IVec3 {
    return Vec3.mul(Vec3.normalized(a), len)
  }

  static normalized(a: IVec3): IVec3 {
    const invLen = 1 / Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
    return {
      x: a.x * invLen,
      y: a.y * invLen,
      z: a.z * invLen,
    };
  }

  static negate(a: IVec3): IVec3 {
    return {
      x: -a.x,
      y: -a.y,
      z: -a.z,
    };
  }

  static rotateX(a: IVec3, angleRad: number): IVec3 {
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    return { x: a.x, y: a.y * c - a.z * s, z: a.y * s + a.z * c };
  }

  static rotateY(a: IVec3, angleRad: number): IVec3 {
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    return { x: a.x * c + a.z * s, y: a.y, z: -a.x * s + a.z * c };
  }

  static rotateZ(a: IVec3, angleRad: number): IVec3 {
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    return { x: a.x * c - a.y * s, y: a.x * s + a.y * c, z: a.z };
  }

  static calcAngle(pre: IVec3, a: IVec3, next: IVec3): Radian {
    const ap = Vec3.dir(a, pre);
    const an = Vec3.dir(a, next);
    const angle = Math.acos(Vec3.dot(ap, an));
    return angle;
  }

  static applyMatrix4(v: IVec3, m: IMat4): IVec3 {
    const { x, y, z } = v;
    const w = 1 / (m[3] * x + m[7] * y + m[11] * z + m[15]);

    return {
      x: (m[0] * x + m[4] * y + m[8] * z + m[12]) * w,
      y: (m[1] * x + m[5] * y + m[9] * z + m[13]) * w,
      z: (m[2] * x + m[6] * y + m[10] * z + m[14]) * w,
    };
  }

  static transformDirection(v: IVec3, m: IMat4): IVec3 {
    return Vec3.normalized(Vec3.applyRotation(v, m));
  }

  static applyRotation(v: IVec3, m: IMat4): IVec3 {
    const { x, y, z } = v;

    return {
      x: (m[0] * x + m[4] * y + m[8] * z),
      y: (m[1] * x + m[5] * y + m[9] * z),
      z: (m[2] * x + m[6] * y + m[10] * z),
    };
  }

  static applyQuat(v: IVec3, q: IQuat): IVec3 {
    const { x, y, z } = v;
    const qx = q.x, qy = q.y, qz = q.z, qw = q.w;

    // calculate quat * vector

    const ix = qw * x + qy * z - qz * y;
    const iy = qw * y + qz * x - qx * z;
    const iz = qw * z + qx * y - qy * x;
    const iw = - qx * x - qy * y - qz * z;

    // calculate result * inverse quat
    return Vec3.from(
      ix * qw + iw * - qx + iy * - qz - iz * - qy,
      iy * qw + iw * - qy + iz * - qx - ix * - qz,
      iz * qw + iw * - qz + ix * - qy - iy * - qx
    )
  }

  static xz(vec: IVec3): IVec3 {
    return Vec3.from(vec.x, 0, vec.z);
  }

  static y(vec: IVec3): IVec3 {
    return Vec3.from(0, vec.y, 0);
  }

  static center(points: IVec3[]): IVec3 {
    const sum = Vec3.from(0, 0, 0);
    for (const p of points) {
      sum.x += p.x;
      sum.y += p.y;
      sum.z += p.z;
    }
    sum.x = sum.x / points.length;
    sum.y = sum.y / points.length;
    sum.z = sum.z / points.length;
    return sum;
  }

  static clamp(p: IVec3, min: IVec3, max: IVec3): IVec3 {
    return {
      x: MathUtil.clamp(p.x, min.x, max.x),
      y: MathUtil.clamp(p.y, min.y, max.y),
      z: MathUtil.clamp(p.z, min.z, max.z),
    }
  }

  static isNaN(v: IVec3): boolean {
    return Number.isNaN(v.x) || Number.isNaN(v.y) || Number.isNaN(v.z);

  }

  static ZERO: IVec3 = { x: 0, y: 0, z: 0 };
  static ONE: IVec3 = { x: 1, y: 1, z: 1 };
  static Y: IVec3 = { x: 0, y: 1, z: 0 };
}
